import { PrinterIcon } from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { cn } from "@bednbody/ui/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import {
  type GiftpassOrder,
  PRINT_LABEL,
  PRINT_STEPS,
  type PrintStatus,
  printPages,
  printStep,
} from "./giftpass-orders.model";
import { useGiftpassOrders, useSetPrintStatus } from "./giftpass-orders.hooks";
import { type PrintJob, printCards } from "./print-cards";

/** Every code of the given orders, in the order's own code order. */
async function loadJobs(orders: GiftpassOrder[]): Promise<PrintJob[]> {
  return Promise.all(
    orders.map(async (order) => {
      const { data: batches, error } = await supabase
        .from("giftpass_batches")
        .select("id")
        .eq("order_id", order.id);
      if (error) throw error;
      const ids = (batches ?? []).map((b) => b.id);
      const codes: string[] = [];
      for (let from = 0; ids.length > 0; from += 1000) {
        const { data, error: passError } = await supabase
          .from("giftpasses")
          .select("display_code")
          .in("batch_id", ids)
          .order("display_code")
          .range(from, from + 999);
        if (passError) throw passError;
        codes.push(...(data ?? []).map((p) => p.display_code));
        if (!data || data.length < 1000) break;
      }
      return { order, codes };
    }),
  );
}

export function PrintQueueTab() {
  const { data: orders = [] } = useGiftpassOrders();
  const setPrint = useSetPrintStatus();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [loading, setLoading] = useState(false);

  const queue = orders.filter((o) => o.status === "paid" && o.physical && o.print_status);
  const toPrint = queue.filter((o) => o.print_status === "to_print");
  const chosen = toPrint.filter((o) => picked.has(o.id));
  const cards = chosen.reduce((sum, o) => sum + o.total_passes, 0);
  const pages = printPages(chosen.map((o) => o.total_passes));
  const allPicked = toPrint.length > 0 && chosen.length === toPrint.length;

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const move = (list: GiftpassOrder[], to: PrintStatus, done?: () => void) =>
    setPrint.mutate(
      { orderIds: list.map((o) => o.id), printStatus: to },
      {
        onSuccess: () => {
          toast.success(
            list.length === 1
              ? `${list[0]?.order_code}: ${PRINT_LABEL[to]}`
              : `${list.length} orders moved to ${PRINT_LABEL[to]}`,
          );
          done?.();
        },
        onError: (error) => toast.error(error.message),
      },
    );

  const print = async () => {
    setLoading(true);
    try {
      const jobs = await loadJobs(chosen);
      if (!printCards(jobs)) {
        toast.error("The browser blocked the print window. Allow pop-ups for this site.");
        return;
      }
      setAsking(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't load the codes.");
    } finally {
      setLoading(false);
    }
  };

  const closeBatch = () => {
    setBatchOpen(false);
    setAsking(false);
  };

  return (
    <div className="space-y-6">
      <SurfaceCard className="flex flex-wrap items-center gap-3 p-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={allPicked}
            disabled={toPrint.length === 0}
            onCheckedChange={() =>
              setPicked(allPicked ? new Set() : new Set(toPrint.map((o) => o.id)))
            }
          />
          Select all in To print
        </label>
        <Button disabled={chosen.length === 0} onClick={() => setBatchOpen(true)}>
          <PrinterIcon data-icon="inline-start" /> Print selected
        </Button>
        <Button
          variant="outline"
          disabled={chosen.length === 0 || setPrint.isPending}
          onClick={() => move(chosen, "printed", () => setPicked(new Set()))}
        >
          Mark selected printed
        </Button>
        <p className="text-sm text-muted-foreground">
          {chosen.length
            ? `${chosen.length} orders · ${cards} cards · ${pages} pages`
            : "Tick orders to print them together"}
        </p>
      </SurfaceCard>

      <div className="grid gap-4 md:grid-cols-3">
        {PRINT_STEPS.map((step) => {
          const lane = queue.filter((o) => o.print_status === step);
          return (
            <section key={step} className="space-y-3 rounded-card bg-muted/50 p-3">
              <h2 className="px-1 text-sm font-semibold">
                {PRINT_LABEL[step]}{" "}
                <span className="font-normal text-muted-foreground">{lane.length}</span>
              </h2>
              {lane.length === 0 ? (
                <p className="px-1 text-sm text-muted-foreground">Nothing here.</p>
              ) : null}
              <ul className="space-y-3">
                {lane.map((o) => {
                  const next = printStep(o.print_status, 1);
                  const back = printStep(o.print_status, -1);
                  return (
                    <li key={o.id}>
                      <SurfaceCard
                        className={cn(
                          "space-y-2 p-3",
                          picked.has(o.id) ? "ring-2 ring-primary" : null,
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-mono font-semibold">{o.order_code}</p>
                          {step === "to_print" ? (
                            <Checkbox
                              aria-label={`Select ${o.order_code} for printing`}
                              checked={picked.has(o.id)}
                              onCheckedChange={() => toggle(o.id)}
                            />
                          ) : null}
                        </div>
                        <p className="text-sm">
                          {o.buyer_name} · {o.package_name}
                          {o.quantity > 1 ? ` × ${o.quantity}` : ""}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {o.total_passes} cards · {o.delivery_address}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {next ? (
                            <Button
                              size="sm"
                              disabled={setPrint.isPending}
                              onClick={() => move([o], next)}
                            >
                              Mark {PRINT_LABEL[next].toLowerCase()}
                            </Button>
                          ) : null}
                          {back ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={setPrint.isPending}
                              onClick={() => move([o], back)}
                            >
                              Back
                            </Button>
                          ) : null}
                        </div>
                      </SurfaceCard>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <Dialog open={batchOpen} onOpenChange={(open) => (open ? setBatchOpen(true) : closeBatch())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Print {cards} cards</DialogTitle>
            <DialogDescription>
              {chosen.length} orders · {pages} pages. 6 cards per A4 page (landscape) with cut
              lines. Each order starts on a new page with a cover sheet: order code, buyer, address
              and card count.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 rounded-lg bg-muted p-3 text-sm">
            {chosen.map((o) => (
              <li key={o.id} className="flex justify-between gap-2">
                <span className="font-mono">{o.order_code}</span>
                <span>{o.buyer_name}</span>
                <span className="text-muted-foreground">{o.total_passes} cards</span>
              </li>
            ))}
          </ul>
          {asking ? (
            <p className="rounded-lg bg-primary/10 p-3 text-sm">
              Sent to the printer. Did all {cards} cards print OK?
            </p>
          ) : null}
          <DialogFooter>
            {asking ? (
              <>
                <Button variant="outline" onClick={closeBatch}>
                  Not yet, keep in To print
                </Button>
                <Button
                  disabled={setPrint.isPending}
                  onClick={() =>
                    move(chosen, "printed", () => {
                      setPicked(new Set());
                      closeBatch();
                    })
                  }
                >
                  Yes, mark printed
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={closeBatch}>
                  Back
                </Button>
                <Button disabled={loading} onClick={print}>
                  <PrinterIcon data-icon="inline-start" /> Print
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
